import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Banner from "../../components/ui/Banner/Banner.jsx";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import { STEPS } from "../../data/rates.js";
import { dueDateRows } from "../../data/taxPeriods.js";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema } from "../../seo/schema.js";

const STEP_DETAIL = {
  "Tax preparer": {
    body: "Filing for a client? Enter your PTIN, credentials (CPA, EA, attorney, AFSP, or none), and firm details. Skip this step if you're filing for your own business.",
  },
  Business: {
    body: "Your EIN, legal name, address, and the signing authority who'll e-sign the return. You can't file Form 2290 with a Social Security number — an EIN is required.",
  },
  Form: {
    body: "Original return, amended return, VIN correction, or final return — pick one, and we'll show only the fields that apply.",
  },
  Trucks: {
    body: "Add each vehicle's VIN and taxable gross weight category (A–V), or mark it as logging (reduced rate) or suspended (5,000 miles or less — 7,500 for agricultural — expected during the period).",
  },
  "Prior year": {
    body: "Optional — only if you reported suspended vehicles on last year's Form 2290. Confirm they stayed under the mileage limit, or note any that were sold.",
  },
  Credits: {
    body: "Claim a credit if you already paid tax on a vehicle that was sold, destroyed, stolen, or stayed under the mileage limit last period.",
  },
  "Tax summary": {
    body: "See the tax for every truck, computed straight from the IRS TY2026 rate tables, and the total balance due before you commit to anything.",
  },
  Review: {
    body: "A final read-through of every line of the return — business info, vehicles, and totals — against what you entered.",
  },
  Pay: {
    body: "Pay the tax due by electronic funds withdrawal, EFTPS, or credit/debit card. Form 2290 requires the tax to be paid in full when you file.",
  },
  "Sign & submit": {
    body: "E-sign and transmit. Once the IRS accepts your return, your stamped Schedule 1 — proof of payment for state registration — is ready, usually within seconds.",
  },
};

// Due dates now come from src/data/taxPeriods.js — the single source of truth,
// transcribed from the IRS instructions and cross-checked at build time by
// scripts/check-tax-period.mjs.

const meta = ROUTE_META["/how-it-works"];

const HowItWorks = () => (
  <div className="max-w-[1180px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/how-it-works"
        keywords={meta.keywords}
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How it works", path: "/how-it-works" }])]}
      />
    <PageHeader eyebrow="How it works" title="From first truck to stamped Schedule 1">
      Every EasyFile2290 return follows the same flow — the same order the IRS asks for the information on Form 2290
      and Schedule 1.
    </PageHeader>

    <MarketingArt src="how-it-works.webp" className="w-full max-w-[600px] h-auto mt-7" />

    <div className="grid gap-3.5 mt-8 max-w-[720px]">
      {STEPS.map((label, i) => (
        <Panel key={label} padded pop className="flex gap-4 items-start">
          <span className="w-[34px] h-[34px] rounded-lg border-2 border-[var(--accent)] text-[var(--accent)] grid place-items-center font-disp font-semibold flex-none">
            {i + 1}
          </span>
          <div>
            <b className="font-disp text-[17px]">{label}</b>
            <p className="text-[var(--muted)] text-[15px] leading-[1.6] mt-1.5">{STEP_DETAIL[label]?.body}</p>
          </div>
        </Panel>
      ))}
    </div>

    <Banner tone="info" className="mt-6 max-w-[720px]" icon={<Zap size={18} />}>
      Filing 25 or more vehicles (taxed, not suspended) in the period? Electronic filing is required by the IRS.
      Under 25, it's optional but still the fastest way to get your Schedule 1.
    </Banner>

    {/* Deadlines */}
    <div className="mt-[54px]">
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        When to file
      </div>
      <h2 className="text-[26px] mt-1.5 font-disp">Due the last day of the month after first use</h2>
      <p className="text-[var(--muted)] mt-1.5 max-w-[68ch]">
        Form 2290 must be filed for the month the taxable vehicle is first used on public highways during the
        period — not tied to your state registration renewal date. If a due date falls on a weekend or legal
        holiday, file by the next business day.
      </p>

      <Panel className="overflow-hidden mt-5 max-w-[560px]">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["First used during…", "File & pay by…"].map((h) => (
                <th
                  key={h}
                  className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Each row carries its own month AND deadline, so the two can no
                longer drift. This previously zipped MONTHS[i] with DUE_DATES[i]
                by index — any edit to either array silently shifted every row
                onto the wrong filing deadline. */}
            {dueDateRows().map((row) => (
              <tr key={row.month}>
                <td className="p-3 border-b border-[var(--line)]">{row.monthLabel}</td>
                <td className="p-3 border-b border-[var(--line)] font-mono text-[13px]">{row.dueLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>

    <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-6 mt-[54px] !p-[30px]">
      <div>
        <h2 className="text-[22px] font-disp">Ready to file your return?</h2>
        <p className="text-[var(--muted)] mt-1">Create an account and add your first truck in a few minutes.</p>
      </div>
      <Button as={Link} to="/login?tab=up" size="lg" className="no-underline flex-none">
        Get started free <ArrowRight size={17} />
      </Button>
    </Panel>
  </div>
);

export default HowItWorks;
